import React from "react";
import ComingSoonPage from "../../components/ComingSoonPage";

// Each export is a separate "Coming Soon" page for a feature that doesn't exist yet

export const TrialComparisonComingSoon = () => (
  <ComingSoonPage
    title="Trial Comparison Tool"
    description="Compare multiple clinical trials side-by-side."
    features={[
      "Compare up to 3 trials simultaneously",
      "Side-by-side eligibility criteria",
      "Export comparison reports",
    ]}
  />
);

export const EligibilityCheckerComingSoon = () => (
  <ComingSoonPage
    title="Eligibility Checker"
    description="Quick pre-screening for trial eligibility."
    features={[
      "Quick 5-minute questionnaire",
      "Instant eligibility assessment",
      "Save results to profile",
    ]}
  />
);

// ✅ AI Trial Match doesn't exist yet - this is the "Coming Soon" page for it
export const AITrialMatchingComingSoon = () => (
  <ComingSoonPage
    title="AI Trial Matching"
    description="Advanced AI-powered trial matching with natural language processing."
    features={[
      "Natural language eligibility parsing",
      "Deep learning match optimization",
      "Explainable AI reasoning",
      "Continuous learning from feedback",
    ]}
  />
);

export const GlossaryComingSoon = () => (
  <ComingSoonPage
    title="Clinical Trial Glossary"
    description="Understand medical terminology and clinical trial jargon."
    features={[
      "Searchable medical dictionary",
      "Audio pronunciations",
      "Bookmark favorite terms",
    ]}
  />
);

export const TravelPlannerComingSoon = () => (
  <ComingSoonPage
    title="Travel Planner"
    description="Plan and budget travel to clinical trial sites."
    features={[
      "Distance and route calculation",
      "Travel cost estimation",
      "Accommodation recommendations",
    ]}
  />
);

export const DocumentManagerComingSoon = () => (
  <ComingSoonPage
    title="Document Manager"
    description="Securely store and organize medical records."
    features={[
      "HIPAA-compliant cloud storage",
      "Document categorization",
      "Share with providers",
    ]}
  />
);

export const MedicationTrackerComingSoon = () => (
  <ComingSoonPage
    title="Medication Tracker"
    description="Track medications, dosages, and adherence."
    features={[
      "Medication reminders",
      "Drug interaction checker",
      "Refill reminders",
    ]}
  />
);

export const LabTrackerComingSoon = () => (
  <ComingSoonPage
    title="Lab Results Tracker"
    description="Monitor lab values and biomarkers over time."
    features={[
      "Track lab results with graphs",
      "Trend visualization",
      "Share with healthcare team",
    ]}
  />
);

export const TrialCalendarComingSoon = () => (
  <ComingSoonPage
    title="Trial Calendar"
    description="Manage trial appointments and events."
    features={[
      "Appointment scheduling",
      "Email and SMS reminders",
      "Sync with external calendars",
    ]}
  />
);

export const CostCalculatorComingSoon = () => (
  <ComingSoonPage
    title="Cost Calculator"
    description="Estimate costs of trial participation."
    features={[
      "Travel cost estimation",
      "Time off work calculator",
      "Reimbursement tracking",
    ]}
  />
);

export const SecondOpinionComingSoon = () => (
  <ComingSoonPage
    title="Second Opinion Finder"
    description="Find amyloidosis specialists for consultations."
    features={[
      "Searchable specialist directory",
      "Filter by location and expertise",
      "Virtual consultation options",
    ]}
  />
);

export const ForumsComingSoon = () => (
  <ComingSoonPage
    title="Discussion Forums"
    description="Connect with other patients and share experiences."
    features={[
      "Topic-based discussion boards",
      "Private messaging",
      "Expert moderators",
    ]}
  />
);

export const PatientStoriesComingSoon = () => (
  <ComingSoonPage
    title="Patient Stories"
    description="Read inspiring patient journeys."
    features={[
      "Patient testimonials",
      "Video and written stories",
      "Filter by amyloid type",
    ]}
  />
);

export const SupportGroupsComingSoon = () => (
  <ComingSoonPage
    title="Support Groups"
    description="Join private support groups."
    features={[
      "Virtual group meetings",
      "Condition-specific groups",
      "Peer mentorship",
    ]}
  />
);

export const ExpertQAComingSoon = () => (
  <ComingSoonPage
    title="Expert Q&A"
    description="Ask questions to specialists."
    features={[
      "Submit questions to experts",
      "Browse answered questions",
      "Monthly live sessions",
    ]}
  />
);

export const ResearchLibraryComingSoon = () => (
  <ComingSoonPage
    title="Research Library"
    description="Access research papers and clinical studies."
    features={[
      "Searchable research database",
      "Plain language summaries",
      "Save favorite articles",
    ]}
  />
);

export const GuidelinesComingSoon = () => (
  <ComingSoonPage
    title="Treatment Guidelines"
    description="Evidence-based clinical practice guidelines."
    features={[
      "Latest treatment protocols",
      "International guidelines",
      "Downloadable PDFs",
    ]}
  />
);

export const CalculatorsComingSoon = () => (
  <ComingSoonPage
    title="Clinical Calculators"
    description="Medical calculators and risk assessment tools."
    features={[
      "Cardiac risk scores",
      "Disease staging calculators",
      "Save calculation history",
    ]}
  />
);

export const FindDoctorComingSoon = () => (
  <ComingSoonPage
    title="Find a Specialist"
    description="Locate amyloidosis experts near you."
    features={["Specialist directory", "Filter by location", "Patient reviews"]}
  />
);

export const OrganizationsComingSoon = () => (
  <ComingSoonPage
    title="Support Organizations"
    description="Patient advocacy groups and support organizations."
    features={[
      "Organization directory",
      "Financial assistance programs",
      "Educational resources",
    ]}
  />
);

export const UserManagementComingSoon = () => (
  <ComingSoonPage
    title="User Management"
    description="Manage user accounts and roles."
    features={["User account management", "Role assignment", "Activity logs"]}
  />
);

export const SystemSettingsComingSoon = () => (
  <ComingSoonPage
    title="System Settings"
    description="Configure system-wide settings."
    features={["Email notifications", "System maintenance", "Feature flags"]}
  />
);

export const SecurityComingSoon = () => (
  <ComingSoonPage
    title="Security & Permissions"
    description="Manage security settings and access controls."
    features={["Two-factor authentication", "Audit logs", "Password policies"]}
  />
);

export const BatchMatchingComingSoon = () => (
  <ComingSoonPage
    title="Batch Patient Matching"
    description="Match multiple patients to trials simultaneously."
    features={[
      "Bulk patient import",
      "Automated matching",
      "Progress tracking",
    ]}
  />
);

export const PatientAnalyticsComingSoon = () => (
  <ComingSoonPage
    title="Patient Analytics"
    description="Advanced analytics and insights."
    features={[
      "Population demographics",
      "Treatment outcome tracking",
      "Custom reports",
    ]}
  />
);

export const TrialAlertsComingSoon = () => (
  <ComingSoonPage
    title="Trial Alerts"
    description="Get notified about new matching trials."
    features={[
      "Custom alert criteria",
      "Email and SMS notifications",
      "New trial notifications",
    ]}
  />
);
